import React, { ChangeEvent, Component } from "react";
import classnames from "classnames";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { IDeviceConfigurationDocument } from "@formant/core/model/IDeviceConfigurationDocument";
import { IModule } from "@formant/core/model/IModule";
import { IModuleConfiguration } from "@formant/core/model/IModuleConfiguration";
import { IScopeFilter } from "@formant/core/model/IScopeFilter";
import { ITeleopViewConfiguration } from "@formant/core/model/ITeleopViewConfiguration";
import {
    rtcCompressedImageStreamType,
    rtcH264VideoFrameStreamType,
    rtcJointStateStreamType,
} from "@formant/core/model/rtcStreamTypes";
import { Uuid } from "@formant/core/model/Uuid";
import { InputElement } from "@formant/ui/component/common/Field";

import {
    customModuleStore,
    metadataStore,
    observeStore,
    viewStore,
} from "../../stores";
import { sortCaseInsensitive } from "../../utils/sortLowercase";
import { CircleButton } from "../common/CircleButton";
import { Help } from "../common/Help";
import { SelectField } from "../common/SelectField";
import { SimpleModuleConfigurationSchemaFormPopup } from "../Observe/ModuleConfigurationSchemaFormPopup";
import * as styles from "./TeleopViewSelectField.scss";

export interface ITeleopViewSelectFieldProps {
    index: number;
    deviceIds: Uuid[];
    document: IDeviceConfigurationDocument;
    label?: string;
    unselectedLabel?: string;
    className?: string;
    view?: ITeleopViewConfiguration;
    onChangeView: (index: number, view?: ITeleopViewConfiguration) => void;
}

@observer
export class TeleopViewSelectField extends Component<ITeleopViewSelectFieldProps> {
    @observable
    private telemetryStreamNames?: string[];

    @observable
    private currentModule?: IModule;

    @observable
    private currentlyEditingModule?: boolean;

    @observable
    private currentModuleConfigurationId?: string;

    async componentDidUpdate(prevProps: ITeleopViewSelectFieldProps) {
        if (prevProps.view !== this.props.view) {
            await this.loadCurrentModule();
        }
    }

    public async componentDidMount() {
        const { deviceIds, view } = this.props;
        this.currentModuleConfigurationId = view?.configurationId;
        const metadataFilter: IScopeFilter = {
            deviceIds,
            types: [
                "image",
                "localization",
                "location",
                "point cloud",
                "transform tree",
                "bitset",
                "file",
                "json",
                "text",
                "numeric",
                "numeric set",
            ],
            notNames: [
                "$.host.cpu_util_percent",
                "$.host.mem_used_percent",
                "$.host.cpu",
                "$.host.memory",
            ],
            start: observeStore.metadataStart.toISOString(),
            end: observeStore.metadataEnd.toISOString(),
        };

        const telemetryStreamNames =
            await metadataStore.fetchStreamNames(metadataFilter);
        this.telemetryStreamNames =
            telemetryStreamNames.sort(sortCaseInsensitive);
        await this.loadCurrentModule();
    }

    public render() {
        const { view, document, label, className, unselectedLabel } =
            this.props;
        const { telemetryStreamNames } = this;

        const teleopStreamNames = [
            ...(document.teleop?.rosStreams || [])
                .filter(
                    ({ topicType }) =>
                        topicType === "formant/H264VideoFrame" ||
                        topicType === "sensor_msgs/CompressedImage" ||
                        topicType === "sensor_msgs/JointState",
                )
                .map(_ => _.topicName)
                .sort(sortCaseInsensitive),
            ...(document.teleop?.customStreams || [])
                .filter(
                    ({ rtcStreamType }) =>
                        rtcStreamType === rtcCompressedImageStreamType ||
                        rtcStreamType === rtcH264VideoFrameStreamType ||
                        rtcStreamType === rtcJointStateStreamType,
                )
                .map(_ => _.name)
                .sort(sortCaseInsensitive),
            ...(document.teleop?.hardwareStreams || [])
                .filter(
                    ({ rtcStreamType }) =>
                        rtcStreamType === rtcCompressedImageStreamType ||
                        rtcStreamType === rtcH264VideoFrameStreamType,
                )
                .map(_ => _.name)
                .sort(sortCaseInsensitive),
        ];

        const loading = telemetryStreamNames === undefined;

        const modules = customModuleStore.listAll();
        const teleopCustomView = (
            viewStore.listMatching(document.tags || {}) || []
        ).filter(_ => _.url !== undefined && _.url !== null && _.showOnTeleop);

        return (
            <div className={classnames(styles["teleop-view-field"], className)}>
                {label && (
                    <p>
                        {label}{" "}
                        <Help text="Select the video source you'd like as your primary view during teleoperation." />
                    </p>
                )}{" "}
                <div className={styles["teleop-view-field-select"]}>
                    <SelectField
                        small={true}
                        value={view?.streamName}
                        onChange={this.onChange}
                        data-testid={`teleop-select-field-${label}`}
                    >
                        <>
                            <option key="None" value="None">
                                {unselectedLabel || "None"}
                            </option>
                            {teleopStreamNames.map(_ => (
                                <option key={_} value={_}>
                                    {_}
                                </option>
                            ))}
                            <optgroup label="Realtime streams">
                                {teleopStreamNames.map(_ => (
                                    <option key={_} value={_}>
                                        {_}
                                    </option>
                                ))}
                            </optgroup>
                            <optgroup label="Telemetry streams">
                                {loading && (
                                    <option disabled={true}>
                                        Fetching streams...
                                    </option>
                                )}

                                {(telemetryStreamNames || []).map(_ => (
                                    <option
                                        key={_}
                                        value={_}
                                        data-testid={`teleop-stream-select-${_}`}
                                    >
                                        {_}
                                    </option>
                                ))}
                            </optgroup>
                            {teleopCustomView.length > 0 && (
                                <optgroup label="Custom views">
                                    {teleopCustomView.map(v => (
                                        <option
                                            key={v.name}
                                            value={"custom-view:" + v.id}
                                        >
                                            {v.name}
                                        </option>
                                    ))}
                                </optgroup>
                            )}
                            {modules && modules.length > 0 && (
                                <optgroup label="Modules">
                                    {modules.map(v => (
                                        <option
                                            key={v.id}
                                            value={"module:" + v.id}
                                        >
                                            {v.name}
                                        </option>
                                    ))}
                                </optgroup>
                            )}
                        </>
                    </SelectField>
                    {this.currentModule?.configurationSchemaUrl && (
                        <CircleButton icon="edit" onClick={this.editModule} />
                    )}
                    {this.currentModule?.configurationSchemaUrl &&
                        this.currentlyEditingModule && (
                            <SimpleModuleConfigurationSchemaFormPopup
                                schemaUrl={
                                    this.currentModule.configurationSchemaUrl
                                }
                                configurationId={
                                    this.currentModuleConfigurationId
                                }
                                onConfigurationChange={
                                    this.onConfigurationChangeForModule
                                }
                                onCancel={this.onCancelModuleConfiguration}
                            />
                        )}
                </div>
            </div>
        );
    }

    private onCancelModuleConfiguration = () => {
        this.currentlyEditingModule = undefined;
    };

    private onConfigurationChangeForModule = async (config: string) => {
        const { index, view, onChangeView } = this.props;
        let moduleConfig: IModuleConfiguration;
        if (!this.currentModuleConfigurationId) {
            moduleConfig = await customModuleStore.createConfiguration({
                configuration: config,
            });
        } else {
            moduleConfig = await customModuleStore.updateConfiguration(
                this.currentModuleConfigurationId,
                {
                    configuration: config,
                },
            );
        }
        this.currentModuleConfigurationId = moduleConfig.id;
        this.currentlyEditingModule = undefined;

        const newView = {
            streamName: view?.streamName,
            configurationId: moduleConfig.id,
        };

        return onChangeView(index, newView);
    };

    private onChange = (event: ChangeEvent<InputElement>) => {
        const { value } = event.target;
        const { index, onChangeView, view } = this.props;
        if (value === view?.streamName) {
            return;
        }
        if (value !== view?.streamName) {
            this.currentModuleConfigurationId = undefined;
        }

        const newView = value !== "None" ? { streamName: value } : {};
        return onChangeView(index, newView);
    };

    private editModule = async () => {
        if (this.currentModule) {
            this.currentlyEditingModule = true;
        }
    };

    async loadCurrentModule() {
        // remove previous module to avoid clicking edit button on previous module
        this.currentModule = undefined;

        const { view } = this.props;
        if (view?.streamName?.startsWith("module:")) {
            const moduleId = view.streamName.substring("module:".length);
            const module = await customModuleStore.fetch(moduleId);
            if (module) {
                this.currentModule = module;
            } else {
                this.currentModule = undefined;
            }
        } else {
            this.currentModule = undefined;
        }
        this.currentlyEditingModule = false;
        this.currentModuleConfigurationId = this.props.view?.configurationId;
    }
}
